import { motion } from 'framer-motion'

const isItems = [
    { title: "A digital workforce.", description: "100+ workers with roles, skills, memory, and tools. They execute business functions — not just respond to prompts." },
    { title: "An execution engine.", description: "Unlimited workflows running autonomously. Cron schedules, KPI tracking, approval gates. Work gets done while you sleep." },
    { title: "A business operating system.", description: "CRM, content studio, ad manager, browser automation, lead pipeline — unified under one platform with one workforce." },
    { title: "Pay-for-work economics.", description: "You pay for outcomes delivered, not seats licensed. Every dollar tracked to a specific worker, workflow, and result." },
    { title: "Human-governed autonomy.", description: "Workers act independently within boundaries you set. Approve, pause, override, or takeover — at any time." },
    { title: "A creative production house.", description: "Generate images, podcasts, music, and video content. Workers create, review, and publish — all inside the platform." },
]

const isNotItems = [
    { title: "Not a chatbot.", description: "There's no chat window for customers. Workers have jobs, workflows, and KPIs — not conversations with end users." },
    { title: "Not a SaaS tool.", description: "You don't get a login and figure it out. You hire workers that execute outcomes. The platform runs them." },
    { title: "Not a freelancer.", description: "No timezone issues. No scope creep. No invoicing. Digital workers execute defined workflows 24/7/365." },
    { title: "Not an agent framework.", description: "We don't teach you how to build agents. We give you a workforce that already knows how to do the work." },
    { title: "Not a prompt manager.", description: "Workers bring their own context, memory, and tools. You define business goals — they figure out execution." },
    { title: "Not another dashboard.", description: "Dashboards show you data. Poly does the work. The difference is outcomes delivered, not metrics displayed." },
]

export function Identity() {
    return (
        <section
            id="identity"
            className="py-20 px-6 md:px-12 lg:px-16"
        >
            <div className="w-full max-w-7xl mx-auto">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-12"
                >
                    <span className="inline-block font-body text-xs uppercase tracking-widest text-muted-foreground border border-border rounded-full px-4 py-1.5 mb-6">
                        Identity
                    </span>
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground">
                        What Poly Is.
                    </h2>
                </motion.div>

                {/* What Poly IS */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                    {isItems.map((item, i) => (
                        <IdentityCard key={i} index={i} {...item} />
                    ))}
                </div>

                {/* What Poly IS NOT */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="text-center mb-12"
                >
                    <h2 className="font-heading text-4xl md:text-5xl text-foreground">
                        What Poly Is Not.
                    </h2>
                </motion.div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {isNotItems.map((item, i) => (
                        <NotCard key={i} index={i} {...item} />
                    ))}
                </div>
            </div>
        </section>
    )
}

function IdentityCard({ title, description, index }: { title: string; description: string; index?: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: ((index ?? 0) % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            className="p-6 border border-border rounded-2xl hover:border-primary/40 transition-colors duration-300"
        >
            <h3 className="font-heading text-lg text-foreground mb-2">{title}</h3>
            <p className="font-body text-sm text-muted-foreground font-light leading-relaxed">{description}</p>
        </motion.div>
    )
}

function NotCard({ title, description, index }: { title: string; description: string; index?: number }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: ((index ?? 0) % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="p-6 border border-border/50 rounded-2xl opacity-80 hover:opacity-100 transition-opacity duration-300"
        >
            <h3 className="font-heading text-lg text-foreground/70 mb-2">{title}</h3>
            <p className="font-body text-sm text-muted-foreground font-light leading-relaxed">{description}</p>
        </motion.div>
    )
}
